import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar,
  Check,
  Cloud,
  CloudDownload,
  ExternalLink,
  Filter,
  MapPin,
  Plus,
  RefreshCw,
  Trophy,
  Users,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Toast, type ToastKind } from "@/components/Toast";
import { api } from "@/lib/api";
import { formatItalianDateShort, daysBetween } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function PlaytomicImport() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [daysFrom, setDaysFrom] = useState(-7);
  const [daysTo, setDaysTo] = useState(60);
  const [search, setSearch] = useState("");
  const [showImported, setShowImported] = useState(false);
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(null);

  const discover = useQuery({
    queryKey: ["playtomic", "discover", daysFrom, daysTo],
    queryFn: () => api.discoverPlaytomic({ days_from: daysFrom, days_to: daysTo }),
    staleTime: 30_000,
  });

  const importMut = useMutation({
    mutationFn: (vars: { activity_id: string; slug?: string }) =>
      api.importPlaytomic(vars.activity_id, vars.slug, false),
    onSuccess: (res) => {
      setToast({
        kind: "success",
        message: `Creato draft "${res.slug}" ✓`,
      });
      qc.invalidateQueries({ queryKey: ["tournaments"] });
      qc.invalidateQueries({ queryKey: ["playtomic", "discover"] });
      setTimeout(() => nav(`/tournaments/${res.slug}`), 700);
    },
    onError: (e) => {
      const msg =
        (e as { body?: { detail?: string }; message?: string })?.body?.detail ??
        (e as Error)?.message ??
        String(e);
      setToast({ kind: "error", message: `Errore: ${msg}` });
    },
  });

  const items = (discover.data?.items ?? []).filter((it) => {
    if (!showImported && it.already_imported) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return it.name.toLowerCase().includes(s) || it.suggested_slug.includes(s);
    }
    return true;
  });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Tutti i tornei
        </Link>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <CloudDownload className="h-6 w-6 text-court-500" />
            Importa da Playtomic
          </h1>
          <p className="text-sm text-slate-500">
            Tornei rilevati automaticamente dalle booking Playtomic
            (booking_type=TOURNAMENT), raggruppati per activity_id.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => discover.refetch()}
          loading={discover.isFetching}
        >
          <RefreshCw className="h-4 w-4" />
          Aggiorna
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardBody className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto_auto]">
          <div className="relative">
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">da oggi −</Label>
            <Input
              type="number"
              min={-365}
              max={0}
              value={-daysFrom}
              onChange={(e) => setDaysFrom(-Math.max(0, parseInt(e.target.value)))}
              className="w-20"
            />
            <span className="text-xs text-slate-500">gg</span>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">a +</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={daysTo}
              onChange={(e) => setDaysTo(parseInt(e.target.value))}
              className="w-20"
            />
            <span className="text-xs text-slate-500">gg</span>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={showImported}
              onChange={(e) => setShowImported(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-moving-500 focus:ring-moving-500"
            />
            Mostra anche già importati
          </label>
        </CardBody>
      </Card>

      {/* Lista */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Cloud className="h-4 w-4" />
              Tornei Playtomic ({items.length}
              {discover.data && discover.data.total !== items.length
                ? ` / ${discover.data.total}`
                : ""}
              )
            </CardTitle>
            {discover.isFetching ? (
              <span className="text-xs text-slate-500">caricamento…</span>
            ) : null}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {discover.isError ? (
            <div className="p-5 text-sm text-red-700">
              Errore Playtomic:{" "}
              {String(
                (discover.error as { body?: { detail?: string } })?.body?.detail ??
                  discover.error,
              )}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              {discover.isLoading ? "Caricamento…" : "Nessun torneo trovato."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it) => {
                const days = daysBetween(new Date(), it.tournament_date);
                return (
                  <li
                    key={it.activity_id}
                    className={cn(
                      "flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:gap-4 md:px-5",
                      it.already_imported && "bg-slate-50",
                    )}
                  >
                    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg border border-court-200 bg-court-50 text-court-700">
                      <span className="text-[10px] font-semibold uppercase">
                        {new Date(it.tournament_date).toLocaleString("it-IT", {
                          month: "short",
                        })}
                      </span>
                      <span className="text-base font-bold leading-none">
                        {new Date(it.tournament_date).toLocaleString("it-IT", {
                          day: "2-digit",
                        })}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">
                          {it.name}
                        </span>
                        {it.already_imported ? (
                          <span className="inline-flex items-center gap-1 rounded bg-moving-100 px-1.5 py-0.5 text-[10px] font-semibold text-moving-700">
                            <Check className="h-3 w-3" />
                            Già importato
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatItalianDateShort(it.tournament_date)}
                        </span>
                        <span>
                          {it.time_start}–{it.time_end}
                        </span>
                        <span>
                          {days > 0
                            ? `fra ${days}g`
                            : days === 0
                              ? "oggi"
                              : `${-days}g fa`}
                        </span>
                        {it.max_players > 0 ? (
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {it.registered_count}/{it.max_players} iscritti
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {it.n_resources} {it.n_resources === 1 ? "campo" : "campi"}
                          </span>
                        )}
                        {it.price_current ? (
                          <span className="font-mono font-semibold text-moving-700">
                            {it.price_current}
                          </span>
                        ) : null}
                        {it.gender && it.gender !== "MIXED" ? (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase">
                            {it.gender === "MALE"
                              ? "M"
                              : it.gender === "FEMALE"
                                ? "F"
                                : it.gender}
                          </span>
                        ) : null}
                        {it.level_description ? (
                          <span className="rounded bg-court-50 px-1.5 py-0.5 text-[10px] text-court-700">
                            liv. {it.level_description}
                          </span>
                        ) : null}
                        {it.tournament_status === "REGISTRATION_OPEN" ? (
                          <span className="rounded bg-moving-100 px-1.5 py-0.5 text-[10px] font-semibold text-moving-700">
                            APERTO
                          </span>
                        ) : it.tournament_status === "REGISTRATION_CLOSED" ? (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                            CHIUSO
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
                        <span className="font-mono" title="activity_id Playtomic">
                          {it.activity_id.slice(0, 13)}…
                        </span>
                        <span>·</span>
                        <span>{it.n_resources} campi · {it.n_slots} slot</span>
                        {it.resource_names.length > 0 ? (
                          <>
                            <span>·</span>
                            <span>{it.resource_names.join(", ")}</span>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex shrink-0 gap-2">
                      <a
                        href={it.playtomic_url_hint}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                      >
                        Playtomic
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      {it.already_imported ? (
                        <Link to={`/tournaments/${it.suggested_slug}`}>
                          <Button variant="ghost" size="sm">
                            Apri →
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() =>
                            importMut.mutate({
                              activity_id: it.activity_id,
                              slug: it.suggested_slug,
                            })
                          }
                          loading={
                            importMut.isPending &&
                            importMut.variables?.activity_id === it.activity_id
                          }
                        >
                          <Plus className="h-4 w-4" />
                          Importa
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        <Trophy className="mr-1 inline h-3.5 w-3.5 text-court-500" />
        Una volta importato il torneo partirà in stato <strong>DRAFT</strong> con
        nome, data, orari, campi e prezzo pre-compilati. Puoi rifinire categorie
        / formula / branding dal wizard standard del torneo, poi eseguire la
        pipeline 9-step come sempre.
      </div>

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}
